import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check } from '@phosphor-icons/react/Check'
import { Info } from '@phosphor-icons/react/Info'
import { Warning } from '@phosphor-icons/react/Warning'
import { X } from '@phosphor-icons/react/X'
import { logsHostMode } from '../lib/apiBase.js'
import { TERM_TOAST_GLASS } from '../pages/cex/terminal/terminalModalTheme.js'
import { useAuth } from './AuthContext.jsx'

const CexToastContext = createContext(
  /** @type {null | CexToastApi} */ (null),
)

/**
 * @typedef {Object} CexToastApi
 * @property {(m: string, a?: number | 'dex' | 'cex' | 'both', b?: number | 'dex' | 'cex' | 'both') => void} showError
 * @property {(m: string, a?: number | 'dex' | 'cex' | 'both', b?: number | 'dex' | 'cex' | 'both') => void} showSuccess
 * @property {(m: string, a?: number | 'dex' | 'cex' | 'both', b?: number | 'dex' | 'cex' | 'both') => void} showInfo
 * @property {(m: string, a?: number | 'dex' | 'cex' | 'both', b?: number | 'dex' | 'cex' | 'both') => void} showWarning
 */

const MAX_TOASTS = 32
const STACK_SHOWN = 6
const STACK_OFFSET_PX = 8
/** One width for all toasts — compact vs old max-w-lg (~32rem). */
const TOAST_SHELL_W = 'w-[min(18rem,calc(100vw-2rem))]'
/** Minimum time before auto-dismiss (unless caller passes a longer duration). */
const MIN_TOAST_MS = 5_000

/**
 * @returns {'dex' | 'cex' | 'both'}
 */
function inferToastSource() {
  const m = logsHostMode()
  if (m === 'both') return 'both'
  return m
}

/**
 * @param {unknown} a
 * @param {unknown} b
 * @returns {{ durationMs: number | undefined, source: 'dex' | 'cex' | 'both' }}
 */
function parseShowExtraArgs(a, b) {
  let durationMs
  /** @type {'dex' | 'cex' | 'both' | undefined} */
  let explicit
  if (typeof a === 'number') {
    durationMs = a
    if (b === 'dex' || b === 'cex' || b === 'both') explicit = b
  } else if (a === 'dex' || a === 'cex' || a === 'both') {
    explicit = a
    if (typeof b === 'number') durationMs = b
  }
  const source = explicit ?? inferToastSource()
  return { durationMs, source }
}

/**
 * @param {{ variant: 'error' | 'success' | 'info' | 'warning' }} props
 */
function ToastGlyph({ variant, small }) {
  const s = small ? 'h-5 w-5' : 'h-6 w-6'
  const ic = small ? 'h-3 w-3' : 'h-3.5 w-3.5'
  if (variant === 'success') {
    return (
      <span className={`${s} flex shrink-0 items-center justify-center rounded-full border border-white/25`}>
        <Check className={`${ic} text-zinc-100`} strokeWidth={2.25} weight="bold" />
      </span>
    )
  }
  if (variant === 'error') {
    return (
      <span className={`${s} flex shrink-0 items-center justify-center rounded-full border border-red-400/35`}>
        <X className={`${ic} text-red-300/90`} strokeWidth={2.25} weight="bold" />
      </span>
    )
  }
  if (variant === 'warning') {
    return (
      <span className={`${s} flex shrink-0 items-center justify-center rounded-full border border-amber-400/35`}>
        <Warning className={`${ic} text-amber-200/90`} weight="fill" />
      </span>
    )
  }
  return (
    <span className={`${s} flex shrink-0 items-center justify-center rounded-full border border-zinc-500/40`}>
      <Info className={`${ic} text-zinc-300`} weight="fill" />
    </span>
  )
}

/**
 * @param {{ t: { variant: 'error' | 'success' | 'info' | 'warning', message: string }, className?: string, compact?: boolean, stacked?: boolean }} props
 */
function ToastLine({ t, className = '', compact, stacked }) {
  return (
    <div
      className={`flex w-full min-w-0 items-center gap-2.5 px-3 py-2 sm:gap-3 sm:px-3.5 sm:py-2.5 ${TERM_TOAST_GLASS} ${className}`}
    >
      <ToastGlyph variant={t.variant} small={Boolean(compact)} />
      <span
        className={`min-w-0 flex-1 text-left text-zinc-200 ${stacked ? 'line-clamp-2' : ''} ${compact ? 'text-[0.7rem] leading-snug' : 'font-mono text-[0.7875rem] leading-snug'}`}
      >
        {t.message}
      </span>
    </div>
  )
}

/**
 * CEX / terminal: stacked toasts; hover = full list.
 * Dismiss is paused while the mouse is over the toast area (`mouseenter` / `mouseleave` — stable across stacked children).
 * After the cursor leaves, the remaining duration is rescheduled and toasts go away.
 */
export function CexToastProvider({ children }) {
  const { isAuthenticated, authInitialized } = useAuth()
  const canShowToasts = authInitialized && isAuthenticated
  const [toasts, setToasts] = useState(
    () =>
      /** @type {Array<{ id: string, variant: string, message: string, source: string }>} */ ([]),
  )
  const [listOpen, setListOpen] = useState(false)
  const timeoutMap = useRef(/** @type {Map<string, ReturnType<typeof setTimeout>>} */ (new Map()))
  const dismissMap = useRef(/** @type {Map<string, number>} */ (new Map()))
  const remainingMsRef = useRef(/** @type {Map<string, number>} */ (new Map()))
  const isHoveringRef = useRef(false)
  const toastsRef = useRef(/** @type {Array<{ id: string, variant: string, message: string }>} */ ([]))
  const listRef = useRef(/** @type {HTMLDivElement | null} */ (null))
  const closeT = useRef(/** @type {ReturnType<typeof setTimeout> | null> */ (null))

  toastsRef.current = toasts

  const visibleToasts = useMemo(() => {
    const mode = logsHostMode()
    return toasts.filter((t) => {
      const s = t.source
      if (mode === 'both') return true
      if (mode === 'dex') return s === 'dex' || s === 'both'
      return s === 'cex' || s === 'both'
    })
  }, [toasts])

  const toastsForUi = useMemo(
    () => (canShowToasts ? visibleToasts : []),
    [canShowToasts, visibleToasts],
  )

  const clearCloseTimer = useCallback(() => {
    if (closeT.current) {
      clearTimeout(closeT.current)
      closeT.current = null
    }
  }, [])

  const clearAllTimeouts = useCallback(() => {
    for (const id of timeoutMap.current.keys()) {
      const t = timeoutMap.current.get(id)
      if (t) clearTimeout(t)
    }
    timeoutMap.current.clear()
  }, [])

  const removeToastId = useCallback((id) => {
    setToasts((prev) => prev.filter((x) => x.id !== id))
    const t = timeoutMap.current.get(id)
    if (t) {
      clearTimeout(t)
      timeoutMap.current.delete(id)
    }
    dismissMap.current.delete(id)
    remainingMsRef.current.delete(id)
  }, [])

  const scheduleOne = useCallback(
    (id) => {
      if (isHoveringRef.current) return
      const tOld = timeoutMap.current.get(id)
      if (tOld) {
        clearTimeout(tOld)
        timeoutMap.current.delete(id)
      }
      const end = dismissMap.current.get(id)
      if (end == null) return
      const left = Math.max(0, end - Date.now())
      if (left <= 0) {
        removeToastId(id)
        return
      }
      const t = setTimeout(() => removeToastId(id), left)
      timeoutMap.current.set(id, t)
    },
    [removeToastId],
  )

  const show = useCallback(
    (variant, message, a, b) => {
      if (!authInitialized || !isAuthenticated) return
      const { durationMs, source } = parseShowExtraArgs(a, b)
      const text = String(message || '').trim() || (variant === 'error' ? 'Error' : 'OK')
      const defaultMs =
        variant === 'error' ? 9_000 : variant === 'warning' ? 5_000 : variant === 'info' ? 4_000 : MIN_TOAST_MS
      const raw = durationMs != null ? durationMs : defaultMs
      const ms = Math.max(MIN_TOAST_MS, raw)
      const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      const endAt = Date.now() + ms
      dismissMap.current.set(id, endAt)
      if (isHoveringRef.current) {
        remainingMsRef.current.set(id, ms)
      }

      setToasts((prev) => {
        const next = [...prev, { id, variant, message: text, source }]
        while (next.length > MAX_TOASTS) {
          const drop = next.shift()
          if (drop) {
            const tid = timeoutMap.current.get(drop.id)
            if (tid) {
              clearTimeout(tid)
              timeoutMap.current.delete(drop.id)
            }
            dismissMap.current.delete(drop.id)
            remainingMsRef.current.delete(drop.id)
          }
        }
        return next
      })

      if (!isHoveringRef.current) {
        scheduleOne(id)
      }
    },
    [scheduleOne, authInitialized, isAuthenticated],
  )

  const showError = useCallback((m, a, b) => show('error', m, a, b), [show])
  const showSuccess = useCallback((m, a, b) => show('success', m, a, b), [show])
  const showInfo = useCallback((m, a, b) => show('info', m, a, b), [show])
  const showWarning = useCallback((m, a, b) => show('warning', m, a, b), [show])

  const clearToastState = useCallback(() => {
    clearAllTimeouts()
    dismissMap.current.clear()
    remainingMsRef.current.clear()
    clearCloseTimer()
    setToasts([])
    setListOpen(false)
  }, [clearAllTimeouts, clearCloseTimer])

  useEffect(() => {
    if (!authInitialized) return
    if (!isAuthenticated) clearToastState()
  }, [authInitialized, isAuthenticated, clearToastState])

  useEffect(
    () => () => {
      clearAllTimeouts()
      dismissMap.current.clear()
      remainingMsRef.current.clear()
      clearCloseTimer()
    },
    [clearAllTimeouts, clearCloseTimer],
  )

  // Close expanded list on outside click
  useEffect(() => {
    if (!listOpen) return
    const onDoc = (e) => {
      const el = listRef.current
      if (el && e.target instanceof Node && !el.contains(e.target)) {
        setListOpen(false)
      }
    }
    document.addEventListener('click', onDoc, true)
    return () => document.removeEventListener('click', onDoc, true)
  }, [listOpen])

  useEffect(() => {
    if (!listOpen) return
    const onEsc = (e) => {
      if (e.key === 'Escape') setListOpen(false)
    }
    document.addEventListener('keydown', onEsc, true)
    return () => document.removeEventListener('keydown', onEsc, true)
  }, [listOpen])

  const stackSlice = toastsForUi.slice(-STACK_SHOWN)
  const stackH = stackSlice.length > 0 ? (stackSlice.length - 1) * STACK_OFFSET_PX + 52 : 0
  const assertive = toastsForUi.some((t) => t.variant === 'error' || t.variant === 'warning')

  const onToastAreaEnter = useCallback(() => {
    isHoveringRef.current = true
    for (const t of toastsRef.current) {
      const { id } = t
      const end = dismissMap.current.get(id)
      if (end != null) {
        remainingMsRef.current.set(id, Math.max(0, end - Date.now()))
      }
      const tmo = timeoutMap.current.get(id)
      if (tmo) {
        clearTimeout(tmo)
        timeoutMap.current.delete(id)
      }
    }
    clearCloseTimer()
    setListOpen(true)
  }, [clearCloseTimer])

  const onToastAreaLeave = useCallback(() => {
    isHoveringRef.current = false
    for (const t of toastsRef.current) {
      const { id } = t
      const r = remainingMsRef.current.get(id)
      if (r != null) {
        dismissMap.current.set(id, Date.now() + r)
        remainingMsRef.current.delete(id)
      }
      scheduleOne(id)
    }
    clearCloseTimer()
    closeT.current = setTimeout(() => setListOpen(false), 180)
  }, [clearCloseTimer, scheduleOne])

  const layer =
    typeof document !== 'undefined' && toastsForUi.length > 0
      ? createPortal(
          <div
            ref={listRef}
            className={`settings-toast pointer-events-auto fixed left-1/2 top-4 z-[200] ${TOAST_SHELL_W} -translate-x-1/2 px-3 sm:px-4`}
            role="status"
            aria-live={assertive ? 'assertive' : 'polite'}
            onMouseEnter={onToastAreaEnter}
            onMouseLeave={onToastAreaLeave}
          >
            {listOpen ? (
              <div
                className={`pointer-events-auto max-h-[min(55dvh,26rem)] w-full min-w-0 overflow-y-auto p-2 ${TERM_TOAST_GLASS}`}
              >
                <p className="px-1.5 pb-2 text-[0.65rem] font-medium uppercase tracking-wider text-zinc-500">
                  All actions ({toastsForUi.length})
                </p>
                <ul className="space-y-0.5">
                  {[...toastsForUi]
                    .reverse()
                    .map((t) => (
                      <li
                        key={t.id}
                        className="flex items-start gap-2 rounded-lg px-1.5 py-1.5 text-left transition-colors hover:bg-zinc-800/50"
                      >
                        <div className="pt-0.5">
                          <ToastGlyph variant={t.variant} small />
                        </div>
                        <span className="min-w-0 break-words font-mono text-[0.75rem] leading-relaxed text-zinc-200">
                          {t.message}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            ) : (
              <div
                className="pointer-events-auto relative mx-auto w-full cursor-default"
                style={{ minHeight: stackH > 0 ? `${stackH}px` : undefined }}
                title="Hover for full list · Tap to expand on touch"
                onTouchStart={() => setListOpen(true)}
                role="presentation"
              >
                {stackSlice.map((t, i) => {
                  const n = stackSlice.length
                  const fromTop = (n - 1 - i) * STACK_OFFSET_PX
                  return (
                    <div
                      key={t.id}
                      className="absolute left-0 right-0 transition-[transform,opacity] duration-150"
                      style={{
                        top: fromTop,
                        zIndex: 10 + i,
                      }}
                    >
                      <ToastLine
                        t={t}
                        stacked
                        className={i < n - 1 ? 'opacity-95' : ''}
                        compact={false}
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>,
          document.body,
        )
      : null

  return (
    <CexToastContext.Provider
      value={{ showError, showSuccess, showInfo, showWarning }}
    >
      {children}
      {layer}
    </CexToastContext.Provider>
  )
}

export function useCexToast() {
  const ctx = useContext(CexToastContext)
  if (!ctx) {
    return {
      showError: (m) => {
        if (typeof window !== 'undefined' && m) window.alert(String(m))
      },
      showSuccess: (m) => {
        if (typeof window !== 'undefined' && m) window.alert(String(m))
      },
      showInfo: (m) => {
        if (typeof window !== 'undefined' && m) window.alert(String(m))
      },
      showWarning: (m) => {
        if (typeof window !== 'undefined' && m) window.alert(String(m))
      },
    }
  }
  return ctx
}
